/**
 * 換行判斷的容許值：同一行內字元的 top 差不超過這個距離（上標、emoji 會有細微位移）
 */
const LINE_TOP_TOLERANCE = 1

/**
 * 量出一段文字每一行在第幾個字結束；行的長度會隨字級與視窗寬度變，只能靠實際渲染量
 * @param node {Text | null} 鏡像段落裡的文字節點，內容是整段完整譯文
 * @param createRange {() => Range} Range 工廠，單元測試時換成假的
 * @return {number[]} 每一行的結束字數（不含），最後一個一定是總字數；空節點回空陣列
 */
export const measureLineEnds = (
	node: Text | null,
	createRange: () => Range = () => document.createRange(),
): number[] => {
	// 沒有文字就沒有行
	if (!node || node.length === 0) {
		return []
	}

	const range = createRange()
	// 環境不支援量 rect（jsdom、極舊瀏覽器）就當成一行，整段會直接顯示
	if (typeof range.getClientRects !== 'function') {
		return [node.length]
	}

	const ends: number[] = []
	// 目前這一行的 top，還沒量到任何字元時是 null
	let lineTop: number | null = null
	// 逐字量位置，top 往下跳一階就是新的一行
	for (let i = 0; i < node.length; i++) {
		range.setStart(node, i)
		range.setEnd(node, i + 1)
		const rect = range.getClientRects()[0]
		// 被收掉的空白量不到 rect，略過
		if (!rect) {
			continue
		}

		if (lineTop === null) {
			lineTop = rect.top
		} else if (rect.top - lineTop > LINE_TOP_TOLERANCE) {
			// 這個字是新一行的開頭，上一行在它之前結束
			ends.push(i)
			lineTop = rect.top
		}
	}

	// 最後一行到文字結尾
	ends.push(node.length)

	return ends
}

/**
 * 依播放進度換算已唸到第幾個字
 * @param length {number} 這一段的總字數
 * @param currentTime {number} 音檔目前播放秒數
 * @param duration {number} 音檔總秒數
 * @return {number | null} 已唸字數；duration 不可用（NaN、0、Infinity、負數）或 currentTime 不是有限數字時回 null，呼叫端整段顯示
 */
export const revealedCharsOf = (
	length: number,
	currentTime: number,
	duration: number,
): number | null => {
	// 長度不明的音檔（串流或還沒載入）沒辦法換算
	if (!Number.isFinite(duration) || duration <= 0 || !Number.isFinite(currentTime)) {
		return null
	}

	// 無條件捨去，夾在 0 到總字數之間
	return Math.min(length, Math.max(0, Math.floor((length * currentTime) / duration)))
}

/**
 * 用字數錨點找出正在唸的是第幾行；剛好唸完一行就算進到下一行
 * @param ends {number[]} 每一行的結束字數
 * @param revealedChars {number} 已唸字數
 * @return {number} 行號，唸到盡頭停在最後一行
 */
export const findLineIndex = (ends: number[], revealedChars: number): number => {
	const index = ends.findIndex((end) => end > revealedChars)

	return index < 0 ? Math.max(0, ends.length - 1) : index
}

/**
 * 算出要揭露到第幾個字：正在唸的那一行再加一行，一定落在行邊界
 * @param ends {number[]} 每一行的結束字數
 * @param revealedChars {number} 已唸字數
 * @return {number | null} cut；沒量到行時回 null
 */
export const revealCut = (ends: number[], revealedChars: number): number | null => {
	// 還沒量到行就沒有邊界可以落
	if (ends.length === 0) {
		return null
	}

	return ends[Math.min(findLineIndex(ends, revealedChars) + 1, ends.length - 1)]
}
